import { useState } from 'react'
import { useTexte } from '../../texte'

import type { Poll } from '../../api/types'
import { usePollAktion } from '../../hooks/queries'
import { fehlertext } from '../../api/client'
import { rueckfrage } from '../../components/Rueckfrage'
import { Abstimmungsblatt } from './Abstimmungsblatt'

/**
 * Eine Abstimmung als Kärtchen im Chat (#56).
 *
 * **Was hier zu sehen ist, entscheidet der Server** und nicht diese Datei.
 * Ein Mitlesender bekommt gar keine absoluten Zahlen mitgeschickt; hier steht
 * deshalb nirgends eine Regel, die etwas verbirgt. Eine Oberfläche, die
 * verbirgt, was sie bekommen hat, verbirgt es nur vor dem, der nicht
 * nachsieht.
 *
 * **Der Balken ist die Anzeige, nicht die Zahl.** Prozente allein liest man
 * nicht gern; ein Balken daneben sagt dasselbe auf einen Blick. Er steht
 * hinter dem Text und nicht darunter — eine eigene Zeile je Antwort machte aus
 * vier Antworten acht Zeilen.
 */
export function Abstimmungskarte({ poll, tripId }: { poll: Poll; tripId: number }) {
  const aktion = usePollAktion(tripId)
  const t = useTexte()
  const [problem, setProblem] = useState<string | null>(null)
  const [blattOffen, setBlattOffen] = useState(false)

  const geschlossen = poll.closed_at !== null
  const darfSchliessen = poll.votes !== undefined && !geschlossen
  // The same rule of the data as for closing: only the author gets `votes`.
  const darfOeffnen = poll.votes !== undefined && geschlossen

  /**
   * **Geschickt wird die ganze Auswahl und nicht ein Umschalten.**
   *
   * Was ankommt, ist danach die Wahl dieses Menschen. Zwei Tipps kurz
   * hintereinander enden damit nicht in einem Mischmasch aus zwei halben
   * Anfragen — der Server ersetzt, er rechnet nicht dazu.
   */
  async function waehle(optionId: number): Promise<void> {
    if (geschlossen) return
    setProblem(null)

    const dabei = poll.my_option_ids.includes(optionId)
    const naechste = poll.multi
      ? dabei
        ? poll.my_option_ids.filter((id) => id !== optionId)
        : [...poll.my_option_ids, optionId]
      : // Bei Einfachauswahl nimmt ein zweiter Tipp auf dieselbe Antwort die
        // Stimme zurück. Sonst gäbe es keinen Weg heraus, und „ich wollte gar
        // nicht abstimmen" wäre eine Sackgasse.
        dabei
        ? []
        : [optionId]

    try {
      await aktion.mutateAsync({ art: 'abstimmen', pollId: poll.id, optionIds: naechste })
    } catch (fehler) {
      setProblem(
        fehlertext(fehler),
      )
    }
  }

  async function schliesse(): Promise<void> {
    const ja = await rueckfrage(
      t.chat.abstimmungSchliessen,
      t.allgemein.schliessen,
    )
    if (!ja) return
    setProblem(null)
    try {
      await aktion.mutateAsync({ art: 'schliessen', pollId: poll.id })
    } catch (fehler) {
      setProblem(fehlertext(fehler))
    }
  }

  async function oeffne(): Promise<void> {
    const ja = await rueckfrage(t.chat.abstimmungOeffnen, t.chat.wiederOeffnen, { art: 'normal' })
    if (!ja) return
    setProblem(null)
    try {
      await aktion.mutateAsync({ art: 'oeffnen', pollId: poll.id })
    } catch (fehler) {
      setProblem(fehlertext(fehler))
    }
  }

  return (
    <div className="abstimmung">
      <p className="abstimmung__frage">{poll.question}</p>

      <ul className="abstimmung__antworten">
        {poll.options.map((option) => (
          <li key={option.id}>
            <button
              type="button"
              className={
                option.mine ? 'abstimmung__antwort abstimmung__antwort--meine' : 'abstimmung__antwort'
              }
              // Eine Abstimmung ist eine Auswahl, keine Handlung -- die Rolle
              // sagt es der Vorlesehilfe, und `aria-pressed` allein täte es
              // nicht: bei Mehrfachauswahl sind es Kästchen, sonst Punkte.
              role={poll.multi ? 'checkbox' : 'radio'}
              aria-checked={option.mine}
              disabled={geschlossen || aktion.isPending}
              onClick={() => void waehle(option.id)}
            >
              {/* Der Balken liegt **hinter** dem Text und nimmt keine Tipps an
                  -- sonst läge er über der Trefferfläche des Knopfes. */}
              {option.percent !== undefined && (
                <span
                  className="abstimmung__balken"
                  style={{ width: `${option.percent}%` }}
                  aria-hidden="true"
                />
              )}
              <span className="abstimmung__text">{option.label}</span>
              {option.percent !== undefined && (
                <span className="abstimmung__prozent">{option.percent} %</span>
              )}
            </button>
          </li>
        ))}
      </ul>

      {/* **Warum hier nichts steht, wenn noch nichts steht.** Wer noch nicht
          abgestimmt hat, sieht keine Prozente -- und soll wissen, warum, statt
          eine leere Stelle für einen Fehler zu halten. */}
      {!poll.shows_percent && (
        <p className="abstimmung__hinweis">
          {t.chat.nochNichtAbgestimmt(poll.multi)}
        </p>
      )}

      <div className="abstimmung__fuss">
        {geschlossen && <span className="abstimmung__zu">{t.chat.geschlossen}</span>}
        {poll.multi && !geschlossen && (
          <span className="abstimmung__mehrfach">{t.chat.mehrfach}</span>
        )}

        {/* **Zahlen und Namen gibt es nur, wo der Server sie mitgeschickt
            hat** — also beim Autor. Der Knopf steht deshalb nicht an einer
            Rolle, sondern an den Daten: keine Liste, kein Knopf. */}
        {poll.votes !== undefined && (
          <button
            type="button"
            className="button button--small button--outline"
            onClick={() => setBlattOffen(true)}
          >
            {t.chat.ergebnisZahl(poll.voters ?? 0)}
          </button>
        )}
        {darfSchliessen && (
          <button
            type="button"
            className="button button--small button--outline"
            disabled={aktion.isPending}
            onClick={() => void schliesse()}
          >
            {t.allgemein.schliessen}
          </button>
        )}
        {darfOeffnen && (
          <button
            type="button"
            className="button button--small button--outline"
            disabled={aktion.isPending}
            onClick={() => void oeffne()}
            data-abstimmung-oeffnen=""
          >
            {t.chat.wiederOeffnen}
          </button>
        )}
      </div>

      {problem && (
        <p className="abstimmung__hinweis" role="alert">
          {problem}
        </p>
      )}

      {blattOffen && <Abstimmungsblatt poll={poll} onZu={() => setBlattOffen(false)} />}
    </div>
  )
}
